/**
 * Light and dark mode, for the app and the share links of its reports. The
 * public site (marketing pages, pricing, blog, login, checkout) is always
 * dark. Dark is the default; a user's choice is saved on their device
 * ("eyecaptain-theme", listed as necessary in src/lib/cookieLibrary.ts) and
 * only once they make one.
 *
 * The theme is a small store rather than React state, for three reasons:
 *
 *   · The class on <html> is set before the first paint by the inline script
 *     in index.html, so a prerendered page never flashes the wrong theme. The
 *     store starts from that class instead of guessing again.
 *   · Prerendered pages are built in dark. useSyncExternalStore renders the
 *     server value ("dark") while hydrating and the real one right after, so
 *     a light visitor gets no hydration mismatch.
 *   · Things mounted outside the router (the toaster) read it too.
 *
 * Everything outside the app stays dark: the public site, embeds (they live
 * inside someone else's page) and the sales deck (/presentation).
 */
import React, { createContext, useContext, useEffect, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';

export type Theme = 'dark' | 'light';

const STORAGE_KEY = 'eyecaptain-theme';
/**
 * The first path segment of every app route. Kept in step with the inline
 * script in index.html, which paints the class before the first frame.
 */
export const APP_SEGMENTS = [
  'workspace', 'dashboard', 'projects', 'project', 'clients', 'settings', 'account', 'plans',
  'experiments', 'new-project', 'onboarding', 'admin', 'analytics-admin',
  'competitor-analysis', 'competitor-results', 'competitor-summary', 'competitors',
  'figma', 'heatmap-analysis', 'heatmap-results', 'technical-analysis',
  'my-brand', 'my-competitors', 'share-earn', 'shared', 'dev',
];
/** Share links of app reports that live under a public first segment. */
const APP_SHARE = /^\/compare\/s\//;
/** Browser chrome colour per theme (the mobile address bar). */
const THEME_COLOR: Record<Theme, string> = { dark: '#060907', light: '#f5f6f9' };

/** True where the user's light/dark choice applies; the rest is always dark. */
export const usesAppTheme = (pathname: string) =>
  APP_SEGMENTS.includes(pathname.split('/')[1] ?? '') || APP_SHARE.test(pathname);
const isForcedDark = (pathname: string) => !usesAppTheme(pathname);

function readInitial(): Theme {
  if (typeof document === 'undefined') return 'dark';
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch { /* storage blocked */ }
  return 'dark';
}

let current: Theme = readInitial();
/** What <html> shows right now: the choice in the app, dark everywhere else. */
let painted: Theme =
  typeof document !== 'undefined' && document.documentElement.classList.contains('light') ? 'light' : 'dark';
const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
const getSnapshot = () => current;
const getPaintedSnapshot = () => painted;
const getServerSnapshot = (): Theme => 'dark';

/** Paint a theme on <html>: the class the tokens hang on, native controls, the address bar. */
function paint(theme: Theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('light', theme === 'light');
  root.classList.toggle('dark', theme === 'dark');
  root.style.colorScheme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme]);
  if (painted !== theme) {
    painted = theme;
    listeners.forEach((l) => l());
  }
}

/** Choose a theme. Saved, applied, and announced to every reader. */
export function setAppTheme(theme: Theme) {
  try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* storage blocked */ }
  if (theme === current) return;
  current = theme;
  if (typeof window === 'undefined' || !isForcedDark(window.location.pathname)) paint(theme);
  listeners.forEach((l) => l());
}

// Another tab changed it: follow.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY) return;
    const next: Theme = e.newValue === 'light' ? 'light' : 'dark';
    if (next === current) return;
    current = next;
    if (!isForcedDark(window.location.pathname)) paint(next);
    listeners.forEach((l) => l());
  });
}

/** The user's chosen theme. Usable anywhere, including outside the router. */
export function useAppTheme(): Theme {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * The theme the current page shows (dark on the public site whatever the
 * choice). For things mounted outside the router, such as the toaster.
 */
export function usePaintedTheme(): Theme {
  return useSyncExternalStore(subscribe, getPaintedSnapshot, getServerSnapshot);
}

interface ThemeContextType {
  /** What the visitor chose. */
  theme: Theme;
  /** What this page shows (the public site stays dark). */
  effectiveTheme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
  /** False where the page ignores the choice (public site, embeds), so no switch is offered. */
  isThemeToggleAllowed: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const theme = useAppTheme();
  const { pathname } = useLocation();
  const forced = isForcedDark(pathname);
  const effectiveTheme: Theme = forced ? 'dark' : theme;

  useEffect(() => { paint(effectiveTheme); }, [effectiveTheme]);

  const value: ThemeContextType = {
    theme,
    effectiveTheme,
    toggleTheme: () => setAppTheme(theme === 'dark' ? 'light' : 'dark'),
    setTheme: setAppTheme,
    isThemeToggleAllowed: !forced,
  };
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** Kept for older imports. */
export function ThemeProviderWithRouter({ children }: { children: React.ReactNode }) {
  return <ThemeProvider>{children}</ThemeProvider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
